import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { 
  Send, 
  ArrowDownLeft,
  ArrowDownUp, 
  Plus, 
  LayoutGrid,
  PiggyBank,
  TrendingUp,
  Gift,
  Globe,
  Receipt,
  QrCode,
  Sparkles,
  Bell,
  X,
  ArrowUpRight,
  CreditCard,
  Scissors
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";
import QuickSendModal from "../components/QuickSendModal";
import WalletTopUpModal from "../components/WalletTopUpModal";
import SmartCards from "../components/SmartCards";

const currencySymbols = {
  JMD: "J$",
  USD: "$",
  GBP: "£",
  CAD: "C$"
};

const currencyColors = {
  JMD: "currency-jmd",
  USD: "currency-usd",
  GBP: "currency-gbp",
  CAD: "currency-cad"
};

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [showTransactions, setShowTransactions] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showTopUp, setShowTopUp] = useState(false);
  const [depositAmount, setDepositAmount] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("JMD");
  const sheetRef = useRef(null);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  // Quick Send Modal state
  const [showQuickSend, setShowQuickSend] = useState(false);
  const [selectedQuickSendContact, setSelectedQuickSendContact] = useState(null);

  // Fetch unread notification count
  const { data: notificationData } = useQuery({
    queryKey: ["notificationCount"],
    queryFn: async () => {
      const response = await axios.get(`${API}/notifications/unread-count`);
      return response.data;
    },
    staleTime: 60000,
  });

  const unreadNotifications = notificationData?.unread_count || 0;
  
  // One-time bell nudge per session
  const [shouldNudge, setShouldNudge] = useState(false);
  
  useEffect(() => {
    if (unreadNotifications > 0 && !sessionStorage.getItem("bell_nudged")) {
      setShouldNudge(true);
      sessionStorage.setItem("bell_nudged", "true");
      setTimeout(() => setShouldNudge(false), 800);
    }
  }, [unreadNotifications]);

  // Fetch wallets
  const { data: wallets, isLoading, refetch } = useQuery({
    queryKey: ["wallets"],
    queryFn: async () => {
      const response = await axios.get(`${API}/wallets`);
      return response.data;
    }
  });

  // Fetch recent transactions
  const { data: transactions } = useQuery({
    queryKey: ["transactions"],
    queryFn: async () => {
      const response = await axios.get(`${API}/transactions?limit=20`);
      return response.data;
    }
  });

  // Fetch health score
  const { data: healthScore } = useQuery({
    queryKey: ["healthScore"],
    queryFn: async () => {
      const response = await axios.get(`${API}/ai/health-score`);
      return response.data;
    }
  });

  const totalBalance = wallets?.reduce((sum, w) => {
    const rates = { JMD: 1, USD: 155.5, GBP: 197.2, CAD: 114.3 };
    return sum + (w.balance * rates[w.currency]);
  }, 0) || 0;

  const handleDeposit = async (walletId) => {
    if (!depositAmount || depositAmount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    try {
      await axios.post(`${API}/wallets/${walletId}/deposit?amount=${depositAmount}`);
      toast.success("Deposit successful!");
      setShowDeposit(false);
      setDepositAmount("");
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Deposit failed");
    }
  };

  // Primary action row items
  const primaryActions = [
    { icon: Send, label: "Send", action: () => navigate("/transfer"), color: "bg-gradient-to-br from-pink-500 to-rose-500" },
    { icon: ArrowDownLeft, label: "Receive", action: () => navigate("/receive"), color: "bg-gradient-to-br from-emerald-500 to-green-500" },
    { icon: Receipt, label: "Bills", action: () => navigate("/bills"), color: "bg-gradient-to-br from-orange-500 to-amber-500" },
    { icon: Globe, label: "Remittance", action: () => navigate("/remittance"), color: "bg-gradient-to-br from-blue-500 to-cyan-500" },
    { icon: LayoutGrid, label: "More", action: () => setShowMoreActions(true), color: "bg-gradient-to-br from-purple-500 to-violet-500" },
  ];

  // More actions (in bottom sheet)
  const moreActions = [
    { icon: Scissors, label: "Split Bill", description: "Split expenses with friends", action: () => { setShowMoreActions(false); navigate("/split-bill"); }, color: "text-pink-400" },
    { icon: PiggyBank, label: "Save", description: "Smart savings with AI round-ups", action: () => { setShowMoreActions(false); navigate("/grow"); }, color: "text-yellow-400" },
    { icon: TrendingUp, label: "Invest", description: "Grow your wealth with fractional shares", action: () => { setShowMoreActions(false); navigate("/grow"); }, color: "text-emerald-400" },
    { icon: Gift, label: "Rewards", description: "Earn points and redeem perks", action: () => { setShowMoreActions(false); navigate("/lifestyle"); }, color: "text-rose-400" },
    { icon: ArrowDownUp, label: "Exchange", description: "Convert between currencies", action: () => { setShowMoreActions(false); navigate("/exchange"); }, color: "text-cyan-400" },
    { icon: QrCode, label: "Scan to Pay", description: "Pay by scanning a QR code", action: () => { setShowMoreActions(false); navigate("/scan"); }, color: "text-purple-400" },
    { icon: CreditCard, label: "Virtual Card", description: "Manage your Stelx card", action: () => { setShowMoreActions(false); navigate("/card"); }, color: "text-blue-400" },
  ];

  // Quick send contacts (mock data with avatars)
  const quickSendContacts = [
    { id: "add", name: "Add", initial: "+", isAdd: true },
    { 
      id: "nina_001", 
      name: "Nina", 
      initial: "N", 
      color: "bg-gradient-to-br from-pink-400 to-rose-500",
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face",
      email: "nina@example.com"
    },
  ];

  // Handle quick send contact click
  const handleQuickSendClick = (contact) => {
    if (contact.isAdd) {
      navigate("/transfer");
    } else {
      setSelectedQuickSendContact(contact);
      setShowQuickSend(true);
    }
  };

  // Get primary wallet (JMD preferred for Jamaica-focused app, fallback to first wallet)
  const primaryWallet = wallets?.find(w => w.currency === "JMD") || wallets?.[0];
  const primaryBalance = primaryWallet?.available_balance || primaryWallet?.balance || 0;
  const primaryCurrency = primaryWallet?.currency || "JMD";

  // Bottom sheet drag handling
  const handleTouchStart = () => {
    setIsDragging(true);
    setDragY(0);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const sheet = sheetRef.current;
    if (sheet) {
      const rect = sheet.getBoundingClientRect();
      const newDragY = Math.max(0, touch.clientY - rect.top);
      setDragY(newDragY);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (dragY > 100) {
      setShowTransactions(false);
      setShowMoreActions(false);
    }
    setDragY(0);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard">
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-[100px]" />
      </div>

      {/* Compact Header */}
      <header className="relative z-10 px-5 pt-4 pb-2 flex items-center justify-between max-w-lg mx-auto">
        <button 
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          onClick={() => navigate("/settings")}
          data-testid="profile-btn"
        >
          <div className="w-10 h-10 rounded-full bg-white/10 border border-white/20 overflow-hidden flex items-center justify-center">
            {user?.picture ? (
              <img src={user.picture} alt={user?.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-white font-semibold">
                {user?.name?.charAt(0) || "U"}
              </span>
            )}
          </div>
          <div className="text-left">
            <p className="text-xs text-muted-foreground">Good evening</p>
            <p className="font-heading font-semibold text-white">
              {user?.name?.split(" ")[0] || "User"}
            </p>
          </div>
        </button>
        <button
          onClick={() => navigate("/notifications")}
          className="relative p-2 rounded-xl hover:bg-white/5 transition-colors"
          data-testid="notification-btn"
        >
          <Bell className={`w-5 h-5 text-muted-foreground ${shouldNudge ? "animate-bell-nudge" : ""}`} />
          {unreadNotifications > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-pink-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
              {unreadNotifications > 9 ? "9+" : unreadNotifications}
            </span>
          )}
        </button>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-5 pb-24 max-w-lg mx-auto">
        {/* Total Balance Card */}
        <div className="relative rounded-3xl p-6 mb-6 overflow-hidden border border-white/[0.08]" 
          style={{ background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.15) 0%, rgba(6, 182, 212, 0.08) 50%, rgba(15, 23, 42, 0.9) 100%)' }}>
          {/* Subtle glow effect */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/10 rounded-full blur-3xl" />
          <div className="relative z-10">
            <p className="text-xs uppercase tracking-widest text-teal-300/70 mb-1">
              Total Balance
            </p>
            <h1 className="font-mono text-4xl font-bold text-white tracking-tight">
              J${totalBalance.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </h1>
            <div className="flex items-center gap-1 mt-2">
              <div className={`w-2 h-2 rounded-full ${healthScore?.score >= 60 ? "bg-emerald-400" : "bg-yellow-400"}`} />
              <span className="text-xs text-muted-foreground">
                Health: {healthScore?.score || 50}/100
              </span>
            </div>
          </div>
        </div>

        {/* Currency Pills - exclude the primary wallet currency */}
        <div className="flex gap-2 justify-center flex-wrap mb-6">
          {isLoading ? (
            <div className="flex gap-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-8 w-20 rounded-xl bg-white/5 animate-pulse" />
              ))}
            </div>
          ) : (
            wallets?.filter(w => w.currency !== primaryCurrency).map((wallet) => (
              <button
                key={wallet.wallet_id}
                data-testid={`wallet-pill-${wallet.currency}`}
                onClick={() => {
                  setSelectedCurrency(wallet.currency);
                  setShowDeposit(true);
                }}
                className="px-4 py-1.5 rounded-xl bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-400
                  flex items-center gap-2 transition-transform hover:scale-105 active:scale-95 shadow-lg shadow-amber-500/20"
              >
                <span className="text-xs font-semibold text-black">{wallet.currency}</span>
                <span className="text-xs text-black/70 font-mono">
                  {currencySymbols[wallet.currency]}{wallet.balance > 1000 
                    ? `${(wallet.balance / 1000).toFixed(1)}k` 
                    : wallet.balance.toFixed(0)}
                </span>
              </button>
            ))
          )}
        </div>

        {/* PRIMARY ACTION ROW */}
        <div className="mb-6">
          <div className="glass rounded-3xl p-3 flex items-center justify-between">
            {primaryActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <button
                  key={index}
                  onClick={action.action}
                  data-testid={`action-${action.label.toLowerCase()}`}
                  className="flex flex-col items-center gap-1.5 group flex-1"
                >
                  <div className={`w-12 h-12 rounded-full ${action.color} flex items-center justify-center 
                    transition-all group-hover:scale-110 group-active:scale-95 shadow-lg`}
                  >
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-[11px] text-muted-foreground font-medium">{action.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Quick Send Section */}
        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
            Quick Send
          </p>
          <div className="flex gap-4 justify-start">
            {quickSendContacts.map((contact, index) => (
              <button
                key={contact.id || index}
                onClick={() => handleQuickSendClick(contact)}
                className="flex flex-col items-center gap-1.5 group"
                data-testid={`quick-send-${contact.name.toLowerCase()}`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center overflow-hidden
                  transition-transform group-hover:scale-110 group-active:scale-95
                  ${contact.isAdd 
                    ? "border-2 border-dashed border-white/20 text-muted-foreground" 
                    : contact.color || "bg-gray-500"}`}
                >
                  {contact.isAdd ? (
                    <Plus className="w-5 h-5" />
                  ) : contact.avatar ? (
                    <img 
                      src={contact.avatar} 
                      alt={contact.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white font-semibold">{contact.initial}</span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">{contact.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Smart Cards Row */}
        <SmartCards />

        {/* Recent Transactions Preview */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              Recent Activity
            </p>
            <button 
              onClick={() => setShowTransactions(true)}
              className="text-xs text-pink-400 font-medium hover:underline"
              data-testid="see-all-transactions"
            >
              See All
            </button>
          </div>
          
          <div className="glass rounded-2xl overflow-hidden">
            {transactions?.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground text-sm">
                No transactions yet
              </div>
            ) : (
              transactions?.slice(0, 3)
                // Filter out internal round-up debit from main wallet
                .filter(tx => tx.description !== "AI Round-Up → SmartPot")
                .slice(0, 3)
                .map((tx, index) => {
                  // Check if this is a SmartPot round-up credit
                  const isRoundUpSavings = tx.description === "AI Round-Up Savings" && tx.type === "credit";
                  
                  return (
                    <div 
                      key={tx.transaction_id || index}
                      className="px-4 py-3 flex items-center justify-between border-b border-white/5 last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isRoundUpSavings 
                            ? "bg-pink-500/20 text-pink-400"
                            : tx.type === "credit" 
                              ? "bg-emerald-500/20 text-emerald-400" 
                              : "bg-rose-500/20 text-rose-400"
                        }`}>
                          {isRoundUpSavings ? (
                            <PiggyBank className="w-4 h-4" />
                          ) : tx.type === "credit" ? (
                            <ArrowDownLeft className="w-4 h-4" />
                          ) : (
                            <ArrowUpRight className="w-4 h-4" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-1">
                            <p className="text-sm font-medium text-white line-clamp-1">
                              {isRoundUpSavings 
                                ? "AI Round-Up Savings" 
                                : tx.description?.split(" ").slice(0, 3).join(" ") || "Transaction"}
                            </p>
                            {isRoundUpSavings && (
                              <Sparkles className="w-3 h-3 text-pink-400" />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {isRoundUpSavings 
                              ? "Saved to SmartPot"
                              : new Date(tx.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                          </p>
                        </div>
                      </div>
                      <span className={`font-mono text-sm font-medium ${
                        isRoundUpSavings 
                          ? "text-pink-400"
                          : tx.type === "credit" 
                            ? "text-emerald-400" 
                            : "text-rose-400"
                      }`}>
                        {tx.type === "credit" ? "+" : "-"}{currencySymbols[tx.currency] || ""}{tx.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  );
                })
            )}
          </div>
        </div>
      </main>

      {/* MORE ACTIONS Bottom Sheet */}
      {showMoreActions && (
        <div 
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={() => setShowMoreActions(false)}
        >
          <div
            ref={sheetRef}
            className="absolute bottom-0 left-0 right-0 bg-card 
              rounded-t-3xl border-t border-white/10 animate-slide-up overflow-hidden"
            style={{ transform: `translateY(${dragY}px)` }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Drag Handle */}
            <div className="flex justify-center py-3 cursor-grab active:cursor-grabbing">
              <div className="w-10 h-1 rounded-full bg-white/20" />
            </div>

            {/* Header */}
            <div className="px-5 pb-4 flex items-center justify-between">
              <h2 className="font-heading font-semibold text-lg text-white">More Actions</h2>
              <button 
                onClick={() => setShowMoreActions(false)}
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-muted-foreground hover:text-white"
                data-testid="close-more-actions"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Action List */}
            <div className="px-5 pb-8 space-y-2">
              {moreActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <button
                    key={index}
                    onClick={action.action}
                    data-testid={`more-action-${action.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="w-full flex items-center gap-4 p-4 rounded-2xl bg-white/5 
                      hover:bg-white/10 transition-colors text-left"
                  >
                    <div className={`w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center ${action.color}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{action.label}</p>
                      <p className="text-xs text-muted-foreground">{action.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Transaction Bottom Sheet */}
      {showTransactions && (
        <div 
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={() => setShowTransactions(false)}
        >
          <div
            ref={sheetRef}
            className="absolute bottom-0 left-0 right-0 max-h-[85vh] bg-card 
              rounded-t-3xl border-t border-white/10 animate-slide-up overflow-hidden"
            style={{ transform: `translateY(${dragY}px)` }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Drag Handle */}
            <div className="flex justify-center py-3 cursor-grab active:cursor-grabbing">
              <div className="w-10 h-1 rounded-full bg-white/20" />
            </div>

            {/* Header */}
            <div className="px-5 pb-4 flex items-center justify-between border-b border-white/10">
              <h2 className="font-heading font-semibold text-lg text-white">Transactions</h2>
              <button 
                onClick={() => setShowTransactions(false)}
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-muted-foreground hover:text-white"
                data-testid="close-transactions"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto max-h-[calc(85vh-80px)] overscroll-contain">
              {transactions?.length === 0 ? (
                <div className="p-10 text-center">
                  <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                    <Receipt className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">No transactions yet</p>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {transactions?.map((tx, index) => (
                    <div 
                      key={tx.transaction_id || index}
                      className="px-5 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          tx.type === "credit" 
                            ? "bg-emerald-500/20 text-emerald-400" 
                            : "bg-rose-500/20 text-rose-400"
                        }`}>
                          {tx.type === "credit" ? (
                            <ArrowDownLeft className="w-5 h-5" />
                          ) : (
                            <ArrowUpRight className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">
                            {tx.description || "Transaction"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(tx.created_at).toLocaleString("en-US", { 
                              month: "short", 
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`font-mono text-sm font-semibold ${
                          tx.type === "credit" ? "text-emerald-400" : "text-rose-400"
                        }`}>
                          {tx.type === "credit" ? "+" : "-"}{currencySymbols[tx.currency] || ""}{tx.amount.toLocaleString()}
                        </span>
                        <p className="text-xs text-muted-foreground capitalize">{tx.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Deposit Modal */}
      {showDeposit && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowDeposit(false)}
        >
          <div 
            className="w-full max-w-sm glass-heavy rounded-3xl p-6 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-semibold text-lg text-white">Add Money</h3>
              <button 
                onClick={() => setShowDeposit(false)}
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-muted-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex gap-2 justify-center">
                {["JMD", "USD", "GBP", "CAD"].map(c => (
                  <button
                    key={c}
                    onClick={() => setSelectedCurrency(c)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      selectedCurrency === c 
                        ? "bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-400 text-black shadow-lg shadow-amber-500/25" 
                        : "bg-white/5 text-muted-foreground hover:bg-white/10"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-muted-foreground">
                  {currencySymbols[selectedCurrency]}
                </span>
                <input
                  type="number"
                  placeholder="0"
                  className="w-full bg-white/5 border border-white/10 rounded-2xl pl-12 pr-4 py-4 
                    text-3xl font-mono text-center text-white focus:outline-none focus:border-pink-500/50"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  data-testid="deposit-amount-input"
                />
              </div>

              <div className="flex gap-2">
                {[100, 500, 1000, 5000].map(amt => (
                  <button
                    key={amt}
                    onClick={() => setDepositAmount(amt.toString())}
                    className="flex-1 py-2 rounded-xl bg-white/5 text-sm text-muted-foreground 
                      hover:bg-white/10 transition-colors"
                  >
                    {amt >= 1000 ? `${amt/1000}k` : amt}
                  </button>
                ))}
              </div>
              
              <Button
                className="w-full btn-primary h-12 text-base"
                onClick={() => {
                  const wallet = wallets?.find(w => w.currency === selectedCurrency);
                  if (wallet) handleDeposit(wallet.wallet_id);
                }}
                data-testid="confirm-deposit-btn"
              >
                Add {currencySymbols[selectedCurrency]}{depositAmount || "0"}
              </Button>

              {/* Stripe Top-up Button */}
              {selectedCurrency === "USD" && (
                <Button
                  variant="outline"
                  className="w-full h-12 text-base border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                  onClick={() => {
                    setShowDeposit(false);
                    setShowTopUp(true);
                  }}
                  data-testid="stripe-topup-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Pay with Card (Stripe)
                </Button>
              )}
              
              <p className="text-xs text-center text-muted-foreground">
                Demo mode - funds are simulated
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Send Modal */}
      <QuickSendModal
        isOpen={showQuickSend}
        selectedRecipient={selectedQuickSendContact}
        balance={primaryBalance}
        currency={primaryCurrency}
        walletId={primaryWallet?.wallet_id}
        onClose={() => {
          setShowQuickSend(false);
          setSelectedQuickSendContact(null);
        }}
      />

      {/* Stripe Top-Up Modal */}
      <WalletTopUpModal
        isOpen={showTopUp}
        onClose={() => setShowTopUp(false)}
        wallet={wallets?.find(w => w.currency === "USD")}
        onSuccess={() => {
          setShowTopUp(false);
          // Refetch wallets
        }}
      />

      <NavBar active="home" />
    </div>
  );
};

export default Dashboard;
